import React from "react";
import {
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Scale,
  Landmark,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { fmtDays, fmtMonths, fmtYears, fmtPct, PALETTE } from "@/lib/chart-utils";
import { cn } from "@/lib/utils";

/**
 * KPICards — 5 top-level metrics for the current profile.
 * Props:
 *   ind   : individual metrics from analyses.results.profiles[i].individual
 *   debt  : analyses.results.debt (may be {available:false})
 */
export default function KPICards({ ind, debt }) {
  const items = [
    {
      key: "days",
      label: "Jours de vie sacrifiés / an",
      emoji: "🔴",
      icon: CalendarClock,
      value: fmtDays(ind?.jours_vie_sacrifies_par_an),
      accent: PALETTE.red,
      testid: "kpi-days",
    },
    {
      key: "months",
      label: "Mois de vie dédiés à l'État",
      emoji: "🟠",
      icon: CalendarDays,
      value: fmtMonths(ind?.mois_vie_par_an),
      accent: "#D97706", // warm orange
      testid: "kpi-months",
    },
    {
      key: "career",
      label: "Années de vie sur carrière",
      emoji: "🟡",
      icon: CalendarRange,
      value: fmtYears(ind?.annees_vie_sur_carriere),
      accent: PALETTE.gold,
      testid: "kpi-career",
    },
    {
      key: "ratio",
      label: "Ratio Vie-Fiscale",
      emoji: "🔵",
      icon: Scale,
      value: fmtPct(ind?.ratio_vie_fiscale_pct, 2),
      accent: PALETTE.blue,
      testid: "kpi-ratio",
    },
    {
      key: "debt",
      label: "Années futures hypothéquées (dette)",
      emoji: "⚫",
      icon: Landmark,
      value: debt?.available
        ? fmtYears(debt?.annees_vie_hypothequees)
        : "—",
      accent: "#111827",
      hint: !debt?.available
        ? "Fournissez la population active à l'étape 3 pour calculer."
        : null,
      testid: "kpi-debt",
    },
  ];
  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5"
      data-testid="kpi-cards"
    >
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <Card
            key={it.key}
            data-testid={it.testid}
            className={cn(
              "relative overflow-hidden border-l-4"
            )}
            style={{ borderLeftColor: it.accent }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  <span aria-hidden="true">{it.emoji}</span>
                  {it.label}
                </div>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div
                className="mt-2 font-mono text-2xl font-bold tabular-nums"
                style={{ color: it.accent }}
              >
                {it.value}
              </div>
              {it.hint && (
                <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                  {it.hint}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
